import React from 'react';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {
  DocSidebarPagePanels,
  StyledFooterDIV,
} from 'app/general/structures/DocSidebarPagePanels';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useSlimTasks} from 'app/tasks/api';
import {TASKS_ALL_PAGE} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {RelatedTaskInstanceItem} from './RelatedTaskInstanceItem';

import type {
  TaskConcreteInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface Props {
  task: TaskConcreteInstanceSerializedType | TaskInstanceSerializedType;
  toggleSidebar: () => unknown;
}

export function TaskDetailPageSidebarRelatedTasks({
  task,
  toggleSidebar,
}: Props): JSX.Element {
  const template_id = task.template.id.toString();

  const {
    data: tasks,
    isFetching,
    refetch,
  } = useSlimTasks({
    template_id,
    due: task.due,
  });

  const {timezone} = useCurrentUserState().data!;

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      runWhenAllRelatedMatch(
        message,
        'taskinstance',
        {
          content_type: 'tasktemplate',
          id: task.template.id,
        },
        () => refetch()
      );
    },
    [template_id]
  );

  return (
    <DocSidebarPagePanels
      title={gt('Tasks in this series')}
      loading={isFetching}
      onClose={toggleSidebar}
      footer={
        <StyledFooterDIV>
          <FullHTMLLink
            className="btn btn-secondary btn-sm"
            html_url={`${TASKS_ALL_PAGE}?template_id=${template_id}`}
          >
            {gt('View on task dashboard')}
          </FullHTMLLink>
        </StyledFooterDIV>
      }
    >
      <ul className="list-group">
        {tasks?.results.map((t) => {
          const isCurrent = t.url === task.url;
          return (
            <li
              key={t.url}
              className={`list-group-item p-0 ${isCurrent ? 'active' : ''}`}
            >
              <RelatedTaskInstanceItem
                isCurrent={isCurrent}
                task={t}
                userTimezone={timezone}
              />
            </li>
          );
        })}
      </ul>
    </DocSidebarPagePanels>
  );
}
